import { Designer, LangObject, Profile, Project, TFile } from "@brg/api";
import { LangObjectArray } from "@brg/api/dist/api-core/utils/lang-utils";
import { ProfileUtils } from "core";

export enum RATINGS_TABS {
  contexts = 'contexts',
  tags = 'tags',
  projects = 'projects',
  people = 'people',
}

/** ratingType API */
export enum RatingsRatingType {
  likes = 120,
  views = 108,
}

/** intervalType API */
export enum RatingsIntervalType {
  allTime = 0,
  day = 1,
  week = 2,
  month = 3,
  module = 4,
  halfYear = 5,
}

export interface RatingsFilter {
  ratingType: RatingsRatingType;
  intervalType: RatingsIntervalType;
  /** Дата для API в формате `YYYY-MM-DD` */
  date: string;
  /** Для UI-лейбла диапазона (неделя/день) */
  dateFrom: string | null;
  dateTo: string | null;
  moduleValue: string | null;
  monthValue: string | null;
}

export interface RatingsApiQuery {
  ratingType: RatingsRatingType;
  intervalType: RatingsIntervalType;
  date: string;
  page: number;
  limit: number;
}

export type NameObjectModel = {
  name?: string;
  lang?: string;
};

/** ratings/api/tag-contexts */
export type ContextRatingsModel = {
  entity?: {
    entityId?: string;
    entityType?: number;
  };
  type?: number;
  value: number;
  category?: {
    categoryType?: number;
    categoryValue?: string;
  };
  position?: number;
};

/** geograffee/api/designer/RatingsForPeriod */
export type DesignerRatingsModel = {
  designerId?: string;
  name?: LangObjectArray;
  surname?: LangObjectArray;
  value: number;
  avatar?: unknown[];
};

export type DesignerModel = {
  designerModel: Designer<'Global'>;
  value: number;
}

/** geograffee/api/project/RatingsForPeriod */
export type ProjectRatingsModel = {
  projectId?: string;
  title?: LangObjectArray;
  value: number;
  appContext?: string;
  authorId?: string
  authorName?: LangObjectArray;
  authorSurName?: LangObjectArray;
  titleImageContainerId?: string;
  titltImageEnContainerId?: string;
  titleImages: TFile[];
  titleImageEn: TFile[];
  titleColor: string;
};

export type ProjectModel = {
  projectModel: Project<'Global'>;
  value: number;
}

export type ProjectRatingModelComputed = {
  id: string;
  projectId: string;
  name: string;
  value: number;
  titleImages: TFile[];
  titleImageEn: TFile[];
  authorName: NameObjectModel[][];
  authorSurName: NameObjectModel[][];
  authorIds: string[];
  titleColor: string;
};

/** tags/api/tag/RatingsForPeriod */
export type TagRatingsModel = {
  tagId?: string;
  name?: NameObjectModel[];
  domain?: string;
  value: number;
  isEducational?: boolean;
};

export const formatRatingsApiDate = (value: Date | string = new Date()): string => {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) {
    return formatRatingsApiDate(new Date());
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const getYesterdayDate = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 1);
  return d;
};

export const DEFAULT_RATINGS_FILTER: RatingsFilter = (() => {
  const yesterday = getYesterdayDate();
  return {
    ratingType: RatingsRatingType.views,
    intervalType: RatingsIntervalType.day,
    date: formatRatingsApiDate(yesterday),
    dateFrom: yesterday.toISOString(),
    dateTo: yesterday.toISOString(),
    moduleValue: null,
    monthValue: null,
  };
})();

/** Месяцы раньше этой даты в фильтре рейтинга недоступны: данных по ним нет. */
export const RATINGS_MONTH_MIN = '2026-08';

/** Дни раньше этой даты в фильтре «день» недоступны. */
export const RATINGS_DAY_MIN = new Date(2026, 8, 1);

/** Понедельник недели, в которую входит 1 августа 2026. Более ранние недели недоступны. */
export const RATINGS_WEEK_MIN = (() => {
  const date = new Date(2026, 7, 1);
  const day = date.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + mondayOffset);
  date.setHours(0, 0, 0, 0);
  return date;
})();

/** Модули раньше этой даты в фильтре недоступны: данных по ним нет. */
export const RATINGS_MODULE_MIN = '2026-07-01';

/** value = YYYY-MM, от августа 2026 до текущего месяца включительно. */
export const getRatingsMonthOptions = (now = new Date()): { value: string }[] => {
  const [minYear, minMonth] = RATINGS_MONTH_MIN.split('-').map(Number);
  const endYear = now.getFullYear();
  const endMonth = now.getMonth() + 1;

  const options: { value: string }[] = [];
  let year = minYear;
  let month = minMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    options.push({ value: `${year}-${String(month).padStart(2, '0')}` });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return options;
};

export const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

export const getRatingsMetricColumnLabel = (
  ratingType: RatingsRatingType,
  t: (key: string) => string,
): string =>
  ratingType === RatingsRatingType.likes
    ? t('ratingsPage.table.likes_for_period')
    : t('ratingsPage.table.views_for_period');

export const monthValueToApiDate = (monthValue: string): string =>
  `${monthValue}-01`;

export const buildRatingsApiQuery = (
  filter: RatingsFilter,
  page = 1,
  limit = 20,
): RatingsApiQuery => ({
  ratingType: filter.ratingType,
  intervalType: filter.intervalType,
  date: filter.date || formatRatingsApiDate(new Date()),
  page,
  limit,
});

export const getLocalizedName = (
  items?: NameObjectModel[] | null,
  language = 'ru',
): string => {
  if (!items?.length) return '';
  const byLang = items.find(
    (item) => (item.lang ?? item.lang)?.toLowerCase() === language.toLowerCase(),
  );
  return (byLang?.name ?? items[0]?.name ?? '').trim();
};

export const normalizeContextsData = (data: ContextRatingsModel[]) =>
  data.map((item, index) => ({
    id: `contexts_${item.entity?.entityId ?? index}`,
    name: item.category?.categoryValue || item.entity?.entityId || '',
    value: item.value,
    contextHref: `${'https://'}${item.entity?.entityId}` || '',
  }));

export const normalizeDesignersData = (
  data: DesignerModel[],
  language = 'ru',
) =>
  data.map(({designerModel, value}, index) => {
    return {
      id: `people_${designerModel.designerId ?? index}`,
      name: designerModel.name,
      surName: designerModel.surName,
      value,
      avatar: ProfileUtils.getAvatarURLOrUndefined(designerModel as unknown as Profile),
      designerId: designerModel.designerId,
    };
  });

export const normalizeProjectsData = (
  data: ProjectModel[],
  language = 'ru',
): ProjectRatingsModel[] =>
  data.map(({projectModel, value}, index) => ({
    id: `projects_${projectModel.projectId ?? index}`,
    projectId: projectModel.projectId ?? '',
    title: projectModel.title,
    value,
    titleImages: projectModel.titleImages,
    titleImageEn: projectModel.titleImages_en,
    authorName: projectModel?.author?.name ?? [],
    authorSurName: projectModel?.author?.middleName ?? [],
    authorId: projectModel?.author?.designerId ?? '',  
    titleColor: projectModel.additionalInfo?.titleColor ?? '',
  }));

export const normalizeTagsData = (
  data: TagRatingsModel[],
  language = 'ru',
) =>
  data.map((item, index) => {
    const isDeletedTag = !item.name && !item.domain

    if (isDeletedTag) {
      return (
        {
          id: `${item.tagId ?? index}`,
          name: language === 'ru' ? 'удаленный тег' : 'deleted tag',
          value: item.value,
          isEducational: item.isEducational || true,
        }
      );
    }

    return (
      {
        id: `${item.tagId ?? index}`,
        name: getLocalizedName(item.name, language) || item.domain || item.tagId || '',
        value: item.value,
        isEducational: item.isEducational || true,
      }
    )
  });
import { Designer, LangObject, Profile, Project, TFile } from "@brg/api";
import { LangObjectArray } from "@brg/api/dist/api-core/utils/lang-utils";
import { ProfileUtils } from "core";

export enum RATINGS_TABS {
  contexts = 'contexts',
  tags = 'tags',
  projects = 'projects',
  people = 'people',
}

/** ratingType API */
export enum RatingsRatingType {
  likes = 120,
  views = 108,
}

/** intervalType API */
export enum RatingsIntervalType {
  allTime = 0,
  day = 1,
  week = 2,
  month = 3,
  module = 4,
  halfYear = 5,
}

export interface RatingsFilter {
  ratingType: RatingsRatingType;
  intervalType: RatingsIntervalType;
  /** Дата для API в формате `YYYY-MM-DD` */
  date: string;
  /** Для UI-лейбла диапазона (неделя/день) */
  dateFrom: string | null;
  dateTo: string | null;
  moduleValue: string | null;
  monthValue: string | null;
}

export interface RatingsApiQuery {
  ratingType: RatingsRatingType;
  intervalType: RatingsIntervalType;
  date: string;
  page: number;
  limit: number;
}

export type NameObjectModel = {
  name?: string;
  lang?: string;
};

/** ratings/api/tag-contexts */
export type ContextRatingsModel = {
  entity?: {
    entityId?: string;
    entityType?: number;
  };
  type?: number;
  value: number;
  category?: {
    categoryType?: number;
    categoryValue?: string;
  };
  position?: number;
};

/** geograffee/api/designer/RatingsForPeriod */
export type DesignerRatingsModel = {
  designerId?: string;
  name?: LangObjectArray;
  surname?: LangObjectArray;
  value: number;
  avatar?: unknown[];
};

export type DesignerModel = {
  designerModel: Designer<'Global'>;
  value: number;
}

/** geograffee/api/project/RatingsForPeriod */
export type ProjectRatingsModel = {
  projectId?: string;
  title?: LangObjectArray;
  value: number;
  appContext?: string;
  authorId?: string
  authorName?: LangObjectArray;
  authorSurName?: LangObjectArray;
  titleImageContainerId?: string;
  titltImageEnContainerId?: string;
  titleImages: TFile[];
  titleImageEn: TFile[];
  titleColor: string;
};

export type ProjectModel = {
  projectModel: Project<'Global'>;
  value: number;
}

export type ProjectRatingModelComputed = {
  id: string;
  projectId: string;
  name: string;
  value: number;
  titleImages: TFile[];
  titleImageEn: TFile[];
  authorName: NameObjectModel[][];
  authorSurName: NameObjectModel[][];
  authorIds: string[];
  titleColor: string;
};

/** tags/api/tag/RatingsForPeriod */
export type TagRatingsModel = {
  tagId?: string;
  name?: NameObjectModel[];
  domain?: string;
  value: number;
  isEducational?: boolean;
};

export const formatRatingsApiDate = (value: Date | string = new Date()): string => {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) {
    return formatRatingsApiDate(new Date());
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const getYesterdayDate = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 1);
  return d;
};

export const DEFAULT_RATINGS_FILTER: RatingsFilter = (() => {
  const yesterday = getYesterdayDate();
  return {
    ratingType: RatingsRatingType.views,
    intervalType: RatingsIntervalType.day,
    date: formatRatingsApiDate(yesterday),
    dateFrom: yesterday.toISOString(),
    dateTo: yesterday.toISOString(),
    moduleValue: null,
    monthValue: null,
  };
})();

/** Месяцы раньше этой даты в фильтре рейтинга недоступны: данных по ним нет. */
export const RATINGS_MONTH_MIN = '2026-08';

/** Дни раньше этой даты в фильтре «день» недоступны. */
export const RATINGS_DAY_MIN = new Date(2026, 8, 1);

/** Понедельник недели, в которую входит 1 августа 2026. Более ранние недели недоступны. */
export const RATINGS_WEEK_MIN = (() => {
  const date = new Date(2026, 7, 1);
  const day = date.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + mondayOffset);
  date.setHours(0, 0, 0, 0);
  return date;
})();

/** Модули раньше этой даты в фильтре недоступны: данных по ним нет. */
export const RATINGS_MODULE_MIN = '2026-07-01';

/** value = YYYY-MM, от августа 2026 до текущего месяца включительно. */
export const getRatingsMonthOptions = (now = new Date()): { value: string }[] => {
  const [minYear, minMonth] = RATINGS_MONTH_MIN.split('-').map(Number);
  const endYear = now.getFullYear();
  const endMonth = now.getMonth() + 1;

  const options: { value: string }[] = [];
  let year = minYear;
  let month = minMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    options.push({ value: `${year}-${String(month).padStart(2, '0')}` });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return options;
};

export const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

export const getRatingsMetricColumnLabel = (
  ratingType: RatingsRatingType,
  t: (key: string) => string,
): string =>
  ratingType === RatingsRatingType.likes
    ? t('ratingsPage.table.likes_for_period')
    : t('ratingsPage.table.views_for_period');

export const monthValueToApiDate = (monthValue: string): string =>
  `${monthValue}-01`;

export const buildRatingsApiQuery = (
  filter: RatingsFilter,
  page = 1,
  limit = 20,
): RatingsApiQuery => ({
  ratingType: filter.ratingType,
  intervalType: filter.intervalType,
  date: filter.date || formatRatingsApiDate(new Date()),
  page,
  limit,
});

export const getLocalizedName = (
  items?: NameObjectModel[] | null,
  language = 'ru',
): string => {
  if (!items?.length) return '';
  const byLang = items.find(
    (item) => (item.lang ?? item.lang)?.toLowerCase() === language.toLowerCase(),
  );
  return (byLang?.name ?? items[0]?.name ?? '').trim();
};

export const normalizeContextsData = (data: ContextRatingsModel[]) =>
  data.map((item, index) => ({
    id: `contexts_${item.entity?.entityId ?? index}`,
    name: item.category?.categoryValue || item.entity?.entityId || '',
    value: item.value,
    contextHref: `${'https://'}${item.entity?.entityId}` || '',
  }));

export const normalizeDesignersData = (
  data: DesignerModel[],
  language = 'ru',
) =>
  data.map(({designerModel, value}, index) => {
    return {
      id: `people_${designerModel.designerId ?? index}`,
      name: designerModel.name,
      surName: designerModel.surName,
      value,
      avatar: ProfileUtils.getAvatarURLOrUndefined(designerModel as unknown as Profile),
      designerId: designerModel.designerId,
    };
  });

export const normalizeProjectsData = (
  data: ProjectModel[],
  language = 'ru',
): ProjectRatingsModel[] =>
  data.map(({projectModel, value}, index) => ({
    id: `projects_${projectModel.projectId ?? index}`,
    projectId: projectModel.projectId ?? '',
    title: projectModel.title,
    value,
    titleImages: projectModel.titleImages,
    titleImageEn: projectModel.titleImages_en,
    authorName: projectModel?.author?.name ?? [],
    authorSurName: projectModel?.author?.middleName ?? [],
    authorId: projectModel?.author?.designerId ?? '',  
    titleColor: projectModel.additionalInfo?.titleColor ?? '',
  }));

export const normalizeTagsData = (
  data: TagRatingsModel[],
  language = 'ru',
) =>
  data.map((item, index) => {
    const isDeletedTag = !item.name && !item.domain

    if (isDeletedTag) {
      return (
        {
          id: `${item.tagId ?? index}`,
          name: language === 'ru' ? 'удаленный тег' : 'deleted tag',
          value: item.value,
          isEducational: item.isEducational || true,
        }
      );
    }

    return (
      {
        id: `${item.tagId ?? index}`,
        name: getLocalizedName(item.name, language) || item.domain || item.tagId || '',
        value: item.value,
        isEducational: item.isEducational || true,
      }
    )
  });
